import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "~/db/db";
import { useGetCreditoCliente } from "~hooks/useSincronizacao";
import { creditoClienteMock } from "../mocks/new/CreditoCliente.mock";
import { meses } from "../utils/formatters";
import { extractUpdatedAt } from "../utils/extractUpdatedAt";
import useVendedor from "~fetchs/useVendedor";
import { usarMock } from "../utils/dataSource";

const formatCurrency = (value) => {
  if (value == null) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
};


const sumCreditFields = (items) =>
  items.reduce(
    (acc, item) => {
      acc.descontoGerencial += item.descontoGerencial ?? 0;
      acc.outrosCreditos += item.outrosCreditos ?? 0;
      acc.total += item.total ?? 0;
      return acc;
    },
    { descontoGerencial: 0, outrosCreditos: 0, total: 0 },
  );

const flattenVendedorClientes = (rawVendedores, vendedorSelecionado) => {
  if (!Array.isArray(rawVendedores) || !rawVendedores.length) return rawVendedores ?? [];

  const isGrouped = rawVendedores.some((v) => Array.isArray(v?.basesCreditoCliente));
  if (!isGrouped) return rawVendedores;

  const filtered =
    vendedorSelecionado !== null
      ? rawVendedores.filter((v) => v?.id === vendedorSelecionado)
      : rawVendedores;

  const allItems = filtered.flatMap((v) => v?.basesCreditoCliente ?? []);

  if (vendedorSelecionado !== null) return allItems;

  const byCode = new Map();
  for (const item of allItems) {
    const key = item.codigo ?? String(item.id);
    if (!byCode.has(key)) byCode.set(key, []);
    byCode.get(key).push(item);
  }

  return Array.from(byCode.values()).map((group) => ({
    ...group[0],
    ...sumCreditFields(group),
  }));
};

const extractRawVendedores = (source) => {
  if (Array.isArray(source?.[0]?.[0]?.data)) {
    const payload = source[0][0];
    if (!payload?.isSuccess) return [];
    return payload.data?.[0] ?? [];
  }
  const arr =
    source?.data?.data?.[0] ??
    source?.data?.[0] ??
    source?.[0] ??
    null;
  return Array.isArray(arr) ? arr : [];
};

const extractPeriod = (clientes) => {
  const first = clientes[0];
  if (!first?.createdAt) return "";
  const date = new Date(first.createdAt);
  return Number.isNaN(date.getTime()) ? "" : `${meses[date.getMonth()]}/${date.getFullYear()}`;
};

const buildCards = (clientes) => {
  const { descontoGerencial, outrosCreditos, total } = sumCreditFields(clientes);
  return [
    { title: "Desconto Gerencial", value: formatCurrency(descontoGerencial), color: "amber" },
    { title: "Outros Créditos",    value: formatCurrency(outrosCreditos),    color: "green" },
    { title: "Crédito Total",      value: formatCurrency(total),             color: "navy"  },
  ];
};

const buildStatsSections = (clientesRota, clientesTodos, period) => {
  const cardsRota = buildCards(clientesRota);
  const cardsTodos = clientesTodos.length > 0 ? buildCards(clientesTodos) : cardsRota;

  return [
    {
      id: "credito-clientes",
      title: "Crédito de clientes",
      period,
      cards: cardsRota,
      actions: [
        { key: "rota",  label: "Clientes da Rota",  active: true,  period,      cards: cardsRota  },
        { key: "todos", label: "Todos os Clientes", active: false, period: null, cards: cardsTodos },
      ],
    },
  ];
};


const listingColumns = [
  { key: "grupoCentralizador", label: "Grupo Centralizador" },
  { key: "cliente",            label: "Cliente" },
  { key: "descontoGerencial",  label: "Desconto Gerencial" },
  { key: "outrosCreditos",     label: "Outros Créditos" },
  { key: "creditoTotal",       label: "Crédito Total" },
];

const buildListingData = (clientes) => ({
  "credito-clientes": {
    columns: listingColumns,
    rows: clientes.map((c) => ({
      id:                    c.id,
      grupoCentralizador:    c.grupoCentralizador ?? "SEM GRUPO",
      cliente:               `${c.codigo} - ${c.razaoSocial}`,
      descontoGerencial:     formatCurrency(c.descontoGerencial ?? 0),
      outrosCreditos:        formatCurrency(c.outrosCreditos ?? 0),
      creditoTotal:          formatCurrency(c.total ?? 0),
      _descontoGerencialRaw: c.descontoGerencial ?? 0,
      _creditoTotalRaw:      c.total ?? 0,
    })),
  },
});

const persistToDb = async (items) => {
  try {
    await db.creditoCliente.clear();
    await db.creditoCliente.add({ payload: items, syncedAt: new Date().toISOString() });
  } catch (err) {
    console.error("Erro ao persistir creditoCliente no Dexie:", err);
  }
};

export const useCustomerCreditData = () => {
  const [allClientes, setAllClientes] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { vendedorSelecionado } = useVendedor();


  const hasMock = useRef(
    usarMock(creditoClienteMock),
  ).current;

  const { data: creditoData, isFetching, isRefetching } =
    useGetCreditoCliente(!hasMock && navigator.onLine, 0, 0, null);

  const loadFromDexie = useCallback(async () => {
    try {
      const rows = await db.creditoCliente.toArray();
      if (rows?.length) setAllClientes(rows[0]?.payload ?? null);
    } catch (err) {
      console.error("Erro ao carregar creditoCliente do Dexie:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasMock) {
      setAllClientes(extractRawVendedores(creditoClienteMock));
      setIsLoading(false);
      return;
    }
    loadFromDexie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasMock) return;
    window.addEventListener("online", loadFromDexie);
    return () => window.removeEventListener("online", loadFromDexie);
  }, [hasMock, loadFromDexie]);

  useEffect(() => {
    if (hasMock || !navigator.onLine) return;

    if (isFetching || isRefetching) {
      setIsRefreshing(true);
      return;
    }

    if (!creditoData) return;

    const items = extractRawVendedores(creditoData);
    setAllClientes(items);
    persistToDb(items);
    setIsRefreshing(false);
    setIsLoading(false);
  }, [hasMock, isFetching, isRefetching, creditoData]);

  const flatClientes = useMemo(
    () => flattenVendedorClientes(allClientes ?? [], vendedorSelecionado),
    [allClientes, vendedorSelecionado],
  );

  const clientes = useMemo(
    () => flatClientes.filter((c) => c.tag === "Rota" || !c.tag),
    [flatClientes],
  );

  const clientesTodos = useMemo(
    () => flatClientes.filter((c) => c.tag === "Todos"),
    [flatClientes],
  );

  const period        = useMemo(() => extractPeriod(clientes), [clientes]);
  const statsSections = useMemo(() => buildStatsSections(clientes, clientesTodos, period), [clientes, clientesTodos, period]);
  const listingData   = useMemo(() => buildListingData(clientes), [clientes]);
  const listingDataTodos = useMemo(() => buildListingData(clientesTodos), [clientesTodos]);

  const updatedAt = useMemo(() => extractUpdatedAt(allClientes), [allClientes]);

  return { clientes, clientesTodos, statsSections, listingData, listingDataTodos, updatedAt, isLoading, isRefreshing };
};
